import { ColDef, ICellRendererParams } from 'ag-grid-community';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUpdateMachineMutation } from '../../../../hooks/mutations/useMachineMutation';
import { useMachinesQuery } from '../../../../hooks/queries/useMachinesQuery';
import { useMachinesTypesQuery } from '../../../../hooks/queries/useMachinesTypesQuery';
import { useAuth } from '../../../../hooks/useAuth';
import { useSystemConfig } from '../../../../hooks/useSystemConfig';
import { Machine } from '../../../../services/apiSLTP/materials/machines';
import { capitalizeFirstLetter, formatName } from '../../../../utils/capitalize';
import { getStatusColor } from '../../../../utils/colors';
import { crudStatus, getCrudStatusInfos, getPriceTypeInfos, localStorageKeys, permissionLevels, systemConfigKeyEnum } from '../../../../utils/enums';
import { formatNumber } from '../../../../utils/numbers';
import StateCell from '../../../cells/StateCell';
import IconLine from '../../../IconLine';
import { InheritedPriceDisplay } from '../../../InheritedPriceDisplay';
import UpdateMachineModal from '../../../modals/admin/material/UpdateMachineModal';
import DeleteModal from '../../../modals/DeleteModal';
import IconButton from '../../../shared/IconButton';
import GenericAgGridTable from '../../GenericAgGridTable';
import { createDateColumn, createDateTimeColumn } from '../../../../utils/dates';

interface MachinesSettingsRowProps {
    users: any[];
    usersIsSuccess?: boolean;
    withUsageCount: boolean;
}

const MachinesSettingsRow: React.FC<MachinesSettingsRowProps> = ({ users, usersIsSuccess = false, withUsageCount }) => {
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedMachine, setSelectedMachine] = useState<Machine | null>(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const { t } = useTranslation();
    const { user } = useAuth();
    const { getConfigValue } = useSystemConfig();

    const {
        data: dataMachines,
        isLoading: isMachinesLoading,
        refetch: refetchMachines,
    } = useMachinesQuery({ queries: { withUsage: withUsageCount } });

    const { data: dataMachineTypes } = useMachinesTypesQuery({});

    const { mutate: mutateMachine } = useUpdateMachineMutation({
        onSuccess: () => refetchMachines(),
        onError: (error) => console.error('Error updating machine:', error.message),
    });

    const sortedMachines = useMemo(() => {
        return (
            dataMachines?.sort((a, b) => {
                const typeA = a.machineTypeId?.displayOrder || 0;
                const typeB = b.machineTypeId?.displayOrder || 0;
                if (typeA < typeB) return -1;
                if (typeA > typeB) return 1;
                const designationA = a?.designation || '';
                const designationB = b?.designation || '';
                return designationA.localeCompare(designationB);
            }) || []
        );
    }, [dataMachines]);

    const columnDefs: ColDef<Machine>[] = [
        { headerName: capitalizeFirstLetter(t('name')), field: 'designation', initialFlex: 1, minWidth: 400, colId: 'name' },
        { headerName: capitalizeFirstLetter(t('codeProC')), field: 'codeProC', colId: 'codeProC', initialWidth: 150 },
        {
            headerName: capitalizeFirstLetter(t('actions')),
            cellRenderer: (params: ICellRendererParams<Machine>) => {
                if (!params.data) return null;
                return (
                    <IconLine>
                        <IconButton
                            iconName="edit"
                            title={t('modifyMachine')}
                            onClick={() => {
                                if (!params.data) return;
                                setSelectedMachine(params.data);
                                setIsEditMode(true);
                                setShowEditModal(true);
                            }}
                            disabled={params.data.crudStatus === crudStatus.DELETED}
                        />
                        {user && user?.profileId.permsLevel <= permissionLevels.ADMIN && (
                            <IconButton
                                iconName="delete"
                                title={t('deleteMachine')}
                                onClick={() => {
                                    if (!params.data) return;
                                    setSelectedMachine(params.data);
                                    setShowDeleteModal(true);
                                }}
                                disabled={params.data.crudStatus === crudStatus.DELETED}
                            />
                        )}
                    </IconLine>
                );
            },
            colId: 'actions',
            filter: false,
            enableRowGroup: false,
            enableValue: false,
            sortable: false,
        },
        {
            headerName: capitalizeFirstLetter(t('status')),
            valueGetter: (params) => capitalizeFirstLetter(t(getCrudStatusInfos(params.data?.crudStatus))),
            cellRenderer: (params: ICellRendererParams<Machine>) => {
                if (!params.data) return params.value;
                return <StateCell stateColor={getStatusColor(params.data?.crudStatus)} text={t(getCrudStatusInfos(params.data?.crudStatus))} />;
            },
            colId: 'status',
        },
        {
            headerName: capitalizeFirstLetter(t('baseName')),
            field: 'baseName',
            colId: 'baseName',
        },
        { headerName: capitalizeFirstLetter(t('type')), field: 'machineTypeId.designation', colId: 'type' },
        {
            headerName: capitalizeFirstLetter(t('user')),
            valueGetter: (params) => formatName(params.data?.userId?.firstName, params.data?.userId?.lastName),
            colId: 'user',
        },
        {
            headerName: `${capitalizeFirstLetter(t('price'))} (${getConfigValue(systemConfigKeyEnum.CURRENCY_SYMBOL)})`,
            filter: 'agNumberColumnFilter',
            valueGetter: (params) => params.data?.price || params.data?.machineTypeId?.price,
            cellRenderer: (params: ICellRendererParams<Machine>) => {
                if (!params.data) return params.value;
                return (
                    <InheritedPriceDisplay
                        directValue={params.data.price}
                        inheritedValue={params.data.machineTypeId?.price}
                        format={(v) => formatNumber(v, t('locale'))}
                    />
                );
            },
            colId: 'price',
            type: 'numericColumn',
        },
        {
            headerName: capitalizeFirstLetter(t('pricing')),
            valueGetter: (params) => capitalizeFirstLetter(t(getPriceTypeInfos(params.data?.priceType || params.data?.machineTypeId?.priceType))),
            cellRenderer: (params: ICellRendererParams<Machine>) => {
                if (!params.data) return params.value;
                return (
                    <InheritedPriceDisplay
                        directValue={params.data.priceType}
                        inheritedValue={params.data.machineTypeId?.priceType}
                        format={(v) => capitalizeFirstLetter(t(getPriceTypeInfos(v)))}
                    />
                );
            },
            colId: 'pricing',
        },
        createDateColumn<Machine>('startDate', 'startDate'),
        createDateColumn<Machine>('endDate', 'endDate'),
        {
            headerName: capitalizeFirstLetter(t('model')),
            field: 'model',
            colId: 'model',
        },
        createDateTimeColumn<Machine>('createdAt', 'createdAt'),
        createDateTimeColumn<Machine>('updatedAt', 'updatedAt'),
    ];

    if (user?.profileId.permsLevel === permissionLevels.ADMIN) {
        columnDefs.push(
            { headerName: t('_id'), field: '_id', initialWidth: 250, colId: '_id', cellClass: 'usage-col-main', headerClass: 'usage-col-main' },
            {
                headerName: t('xTotal'),
                field: 'usageCount.total',
                colId: 'xtotal',
                cellClass: 'usage-col',
                headerClass: 'usage-col',
                filter: 'agNumberColumnFilter',
            },
            {
                headerName: t('xTransfersPlanned'),
                field: 'usageCount.dailyTransfersPlanned',
                colId: 'xtransfersplanned',
                cellClass: 'usage-col',
                headerClass: 'usage-col',
                filter: 'agNumberColumnFilter',
            },
            {
                headerName: t('xTransfersCompleted'),
                field: 'usageCount.dailyTransfersRides',
                colId: 'xtransferscompleted',
                cellClass: 'usage-col',
                headerClass: 'usage-col',
                filter: 'agNumberColumnFilter',
            },
            {
                headerName: t('xMachines'),
                field: 'usageCount.dailyMachines',
                colId: 'xmachines',
                cellClass: 'usage-col',
                headerClass: 'usage-col',
                filter: 'agNumberColumnFilter',
            }
        );
    }

    return (
        <>
            <GenericAgGridTable
                rowData={sortedMachines}
                columnDefs={columnDefs}
                loading={isMachinesLoading}
                headerRightComponent={
                    <IconButton
                        iconName="addCircle"
                        onClick={() => {
                            setSelectedMachine(null);
                            setIsEditMode(false);
                            setShowEditModal(true);
                        }}
                        title={t('addMachine')}
                    />
                }
                exportCsvFileName="admin-machines"
                storageKey={localStorageKeys.TABLE.ADMIN.MATERIAL.MACHINES}
                agGridProps={{ getRowId: ({ data }) => data._id }}
            />

            {showEditModal && (
                <UpdateMachineModal
                    showEditModal={showEditModal}
                    setShowEditModal={setShowEditModal}
                    isEditMode={isEditMode}
                    machine={selectedMachine}
                    machines={dataMachines || []}
                    users={users}
                    machineTypes={dataMachineTypes || []}
                    refetch={refetchMachines}
                />
            )}

            {showDeleteModal && selectedMachine && (
                <DeleteModal
                    open={showDeleteModal}
                    handleClose={() => setShowDeleteModal(false)}
                    deleteAction={() => {
                        mutateMachine({ machineId: selectedMachine._id, data: { crudStatus: crudStatus.DELETED } });
                    }}
                    title={t('machine')}
                    itemName={selectedMachine.designation}
                />
            )}
        </>
    );
};

export default MachinesSettingsRow;
